import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/fanhao';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://jbsms.cn/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://jbsms.cn/category/${activeCategory}`, name: currentCategory?.name || '' } },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '' },
              ]}
            />

            {/* Category Header */}
            <div className="mb-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-10 overflow-x-auto">
              <div className="flex gap-2 pb-2 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Articles Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {article.readTime} 分钟阅读
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {article.publishDate}
                      </span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-search-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}